import { useDataLockerNavItems } from '@/components/navigation/subNav/DataLockerNav';
import { useTeamNavItems } from '@/components/navigation/subNav/TeamNav';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NavigationItem } from '@/types/navigation';
import { selectedNavigationAtom } from '@/atoms/navigation';
import { unwrappedUserDefaultTeamIdsAtom } from '@/atoms/queries/teams';
import { unwrappedDefaultTeamMostRecentGameIdAtom } from '@/atoms/user';
import { useShouldShowHiddenFeature } from '../useShouldShowHiddenFeature';
import { useVideoPlaylistNavigationItem } from './useVideoPlaylistNavigationItem';
import { usePlayerNavigationItem } from './usePlayerNavigationItem';
import { useLogout } from '../useLogout';
import { useShouldShowHybridNav } from './useShouldShowHybridNav';

export const useSinglePlatformNavigationItems = () => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('navigation');
  const { teamId } = useParams();
  const defaultTeamId = useAtomValue(unwrappedUserDefaultTeamIdsAtom)?.datahubId;
  const defaultGameId = useAtomValue(unwrappedDefaultTeamMostRecentGameIdAtom);
  const setSelectedNavAtom = useSetAtom(selectedNavigationAtom);

  const teamPath = `/${locale}/team/${teamId || defaultTeamId}/`;
  const gamePath = `/${locale}/match/${defaultGameId}/`;
  const logout = useLogout();

  // TODO (PPC-1751: unhide game dashboard)
  const shouldShowHiddenFeature = useShouldShowHiddenFeature();
  const shouldShowHybridNav = useShouldShowHybridNav();

  /* istanbul ignore next */
  const gameRoute: NavigationItem[] = shouldShowHiddenFeature
    ? [
        {
          id: 'matches',
          icon: 'MatchSoccer',
          path: `${gamePath}dashboard`,
          label: t('primary.game'),
        },
      ]
    : [];

  const navigationSwitchItem = shouldShowHybridNav
    ? ([
        {
          id: 'switch-nav',
          icon: 'Swap',
          onClick: () => setSelectedNavAtom('crossPlatform'),
          label: t('primary.switchNav'),
        },
      ] as const)
    : [];

  const navigationItems: { primary: NavigationItem[]; footer: NavigationItem[] } = {
    primary: [
      usePlayerNavigationItem(),
      {
        id: 'team',
        icon: 'TeamSoccer',
        path: `${teamPath}dashboard`,
        label: t('primary.team'),
        subNav: useTeamNavItems(teamPath),
      },
      ...gameRoute,
      {
        id: 'data-locker',
        icon: 'IqScout',
        path: `/${locale}/data-locker`,
        label: `${t('primary.dataLocker')}`,
        subNav: useDataLockerNavItems(),
      },
      useVideoPlaylistNavigationItem(),
    ],
    footer: [
      {
        id: 'settings',
        icon: 'AccountSettings',
        path: `/${locale}/settings/default-gender`,
        label: t('primary.settings'),
      },
      {
        id: 'logout',
        icon: 'LogOut',
        onClick: logout,
        label: t('primary.logout'),
      },
      ...navigationSwitchItem,
    ],
  };
  return navigationItems;
};
